import React, { useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";

/* ═══════════════════════════════════════════════════════════
   PrintPdfShell — 원소스 멀티유즈 A4 PDF 쉘
   - 나라장터 보고서, 상세페이지 PDF 모두 동일 쉘 사용
   - 브랜드 헤더/푸터, 연락처, QR코드, 인쇄 최적화
   ═══════════════════════════════════════════════════════════ */

const PRINT_STYLE_ID = "ikoffice-pdf-print-style";

/** 프린트 트리거 — A4 인쇄용 CSS 삽입 후 window.print() */
export function triggerPrint(containerId) {
  // Remove old style if exists
  const old = document.getElementById(PRINT_STYLE_ID);
  if (old) old.remove();

  const style = document.createElement("style");
  style.id = PRINT_STYLE_ID;
  style.textContent = `
    @media print {
      body * { visibility: hidden !important; }
      #${containerId}, #${containerId} * { visibility: visible !important; }
      #${containerId} {
        position: fixed; left: 0; top: 0;
        width: 100%; min-height: 100vh;
        background: white !important;
      }
      .no-print { display: none !important; }
      @page { size: A4; margin: 12mm 15mm; }
      /* 잉크 절약: 배경색 제거 */
      * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  `;
  document.head.appendChild(style);
  window.print();
  setTimeout(() => {
    const el = document.getElementById(PRINT_STYLE_ID);
    if (el) el.remove();
  }, 800);
}

/** PDF 다운로드 버튼 — 재사용 가능 */
export function PdfDownloadButton({ containerId, label = "PDF 다운로드", className = "" }) {
  return (
    <button
      onClick={() => triggerPrint(containerId)}
      className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition ${className || "bg-[#1d1d1f] text-white hover:bg-[#333]"}`}
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
      </svg>
      {label}
    </button>
  );
}

/** QR 코드 with URL — 재사용 가능 */
export function PageQR({ url, size = 64 }) {
  if (!url) return null;
  return (
    <div className="flex flex-col items-center gap-1">
      <QRCodeSVG value={url} size={size} level="M" includeMargin={false} />
      <p className="text-[8px] text-[#86868b] max-w-[80px] text-center truncate">{url.replace(/^https?:\/\//, '')}</p>
    </div>
  );
}

/** 일광엔시스 브랜드 헤더 — A4 PDF 공통 */
export function BrandHeader({ title, subtitle, date, qrUrl }) {
  const today = date || new Date().toLocaleDateString("ko-KR", { year: "numeric", month: "long", day: "numeric" });
  return (
    <div className="flex items-start justify-between border-b-2 border-[#1d1d1f] pb-3 mb-5">
      <div className="flex-1 min-w-0">
        <h1 className="text-[22px] font-bold text-[#1d1d1f] leading-tight truncate">{title}</h1>
        {subtitle && <p className="text-[12px] text-[#6e6e73] mt-0.5">{subtitle}</p>}
      </div>
      <div className="flex items-start gap-3 shrink-0 ml-4">
        {qrUrl && <PageQR url={qrUrl} size={52} />}
        <div className="text-right">
          <p className="text-[10px] text-[#86868b]">{today}</p>
          <p className="text-[11px] font-semibold text-[#1d1d1f] mt-0.5">일광엔시스</p>
          <p className="text-[9px] text-[#86868b]">ikoffices.com · sktec.kr</p>
        </div>
      </div>
    </div>
  );
}

/** 일광엔시스 브랜드 푸터 — A4 PDF 공통 */
export function BrandFooter({ extraText }) {
  return (
    <div className="mt-auto pt-4 border-t border-gray-200">
      <div className="flex justify-between items-end">
        <div className="text-[9px] text-[#86868b]">
          <p className="font-semibold text-[#1d1d1f] text-[10px]">일광엔시스 · 일광오피스</p>
          <p>경기도 안양시 동안구 시민대로 230 평촌아크로타워 A동403호</p>
          <p>사업자등록번호 119-24-00329 | ikoffices.com · sktec.kr</p>
          {extraText && <p>{extraText}</p>}
        </div>
        <div className="text-right text-[#86868b]">
          <p className="text-[13px] font-bold text-[#1d1d1f] tracking-wider">1600-7542</p>
          <p className="text-[9px]">FAX 031-624-4769 · master@sktec.kr</p>
        </div>
      </div>
    </div>
  );
}

/** 전체 A4 쉘 — 모달 형태로 사용 */
export function PdfPreviewModal({ containerId, onClose, children }) {
  useEffect(() => {
    const closeOnEscape = (event) => { if (event.key === "Escape") onClose(); };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);
  return (
    <div role="dialog" aria-modal="true" aria-label="제품 자료 미리보기" className="clone-pdf-modal fixed inset-0 z-[90] flex items-start justify-center bg-black/50 overflow-auto py-8 print:p-0 print:bg-transparent">
      <div className="w-full max-w-[210mm] mx-auto">
        {/* 툴바 */}
        <div className="flex items-center justify-between mb-3 no-print px-2">
          <PdfDownloadButton containerId={containerId} label="PDF 저장 / 인쇄" />
          <button onClick={onClose} className="px-4 py-2 bg-white/90 rounded-lg text-gray-600 text-sm hover:bg-white transition">&times; 닫기</button>
        </div>
        {/* A4 용지 */}
        <div
          id={containerId}
          className="clone-pdf-paper bg-white shadow-2xl print:shadow-none mx-auto flex flex-col"
          style={{ width: "210mm", minHeight: "297mm", padding: "12mm 15mm", fontFamily: "'Pretendard', 'Apple SD Gothic Neo', sans-serif" }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

export default { triggerPrint, PdfDownloadButton, PageQR, BrandHeader, BrandFooter, PdfPreviewModal };
